// web/src/components/QuickSearchOverlay.tsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  collection,
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  where,
} from "firebase/firestore";
import { db, auth } from "@/lib/firebase";
// Break circular import with scryClient; provide a local, reliable image URL helper.
function scryImageUrl(id: string, size: "small" | "normal" | "large" = "small") {
  // Scryfall supports format=image with version param (no hashed path needed)
  const ver = size === "small" ? "small" : size === "large" ? "large" : "normal";
  return `https://api.scryfall.com/cards/${id}?format=image&version=${ver}`;
}
import V2Modal from "./V2Modal";
import { useLocation, useNavigate } from "react-router-dom";
import type { V2Preload } from "@/lib/hydrateForModal";
import { cardsSearch } from "@/lib/scryApiClient";
/* ======================= Types (light) ======================= */

type ScryCard = {
  id: string;
  name: string;
  oracle_id?: string | null;
  set?: string | null;
  set_name?: string | null;
  lang?: string | null;
  collector_number?: string | null;
  prices?: { usd?: string | null; usd_foil?: string | null } | null;
  image_uris?: { small?: string | null; normal?: string | null; large?: string | null } | null;
  card_faces?: Array<{ image_uris?: { small?: string | null; normal?: string | null; large?: string | null } | null }> | null;
};

type ListingCard = {
  id: string;
  ownerUid: string;
  ownerUsername?: string | null;
  name: string;
  image?: string | null;
  status: "sale" | "trade";
  scryfall_id?: string | null;
  oracle_id?: string | null;
};

/* ======================= Human search helpers ======================= */

function normalizeHuman(input: string): string {
  return (input || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[’‘`´]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/[–—−]/g, "-")
    .replace(/[•·]/g, " ")
    .replace(/[._,;:!?()[\]{}]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

// ADD: squish to only [a-z0-9], lowercased (no spaces/punct)
function squishAlphaNum(s: string): string {
  return (s || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

// UPDATE: buildHumanQueries to include de-punctuated variants and "'s" fallback
function buildHumanQueries(term: string): string[] {
  const raw = (term || "").trim();
  const norm = normalizeHuman(raw);
  const unique = new Set<string>();

  const add = (q?: string | null) => {
    if (!q) return;
    const s = String(q).trim();
    if (!s) return;
    unique.add(s);
  };

  // Base candidates (exact and normalized)
  add(raw);
  add(`name:"${raw}"`);
  if (norm !== raw) {
    add(norm);
    add(`name:"${norm}"`);
  }

  // Handle split cards (A/B)
  if (raw.includes("/")) add(raw.replace(/\s*\/+\s*/g, " // "));
  if (norm.includes("/")) add(norm.replace(/\s*\/+\s*/g, " // "));

  // NEW: de-punctuated candidates (e.g., "Thassa's" -> "thassas")
  const dpRaw = squishAlphaNum(raw);      // thassas
  const dpNorm = squishAlphaNum(norm);    // thassas
  if (dpRaw && dpRaw.length >= 3) {
    add(dpRaw);
    add(`name:"${dpRaw}"`);
    // Allow contains-style matching anywhere in the name for 3+ letters
    add(`name:${dpRaw}`);
  }
  if (dpNorm && dpNorm !== dpRaw && dpNorm.length >= 3) {
    add(dpNorm);
    add(`name:"${dpNorm}"`);
    add(`name:${dpNorm}`);
  }

  // NEW: "'s" heuristic — if user typed thassas, also try thassa
  const dropS = (s: string) => (s.endsWith("s") && s.length > 3 ? s.slice(0, -1) : "");
  const dpBase = dropS(dpNorm || dpRaw || "");
  if (dpBase) {
    add(dpBase);                 // thassa
    add(`name:"${dpBase}"`);     // name:"thassa"
    add(`name:${dpBase}`);        // name:thassa (contains)
  }

  // NEW: tokenized contains queries for any 3+ letters anywhere in name
  const tokens = (norm || "").toLowerCase().split(/\s+/).filter(t => t && squishAlphaNum(t).length >= 3);
  for (const t of tokens) {
    const st = squishAlphaNum(t);
    add(st);
    add(`name:${st}`);
    add(`name:"${st}"`);
  }

  // OPTIONAL FOLLOW-UP: intersect tokens (AND) so all must match somewhere in name
  if (tokens.length >= 2) {
    const parts = tokens.map(t => `name:${squishAlphaNum(t)}`);
    add(parts.join(" ")); // implicit AND across tokens
  }

  return Array.from(unique);
}

async function humanSearch(term: string, signal?: AbortSignal): Promise<any[]> {
  const queries = buildHumanQueries(term);
  const out: any[] = [];
  const seen = new Set<string>();
  for (const q of queries) {
    try {
      const res = await apiSearch(q, signal);
      const list = extractCards(await toJson(res));
      for (const c of list) {
        if (c?.id && !seen.has(c.id)) {
          seen.add(c.id);
          out.push(c);
        }
      }
      if (out.length >= 24) break;
    } catch {}
  }
  return out;
}

function dedupeSearchResults(list: any[]): any[] {
  const seen = new Set<string>();
  const out: any[] = [];
  for (const c of list) {
    const key = (c.oracle_id ? String(c.oracle_id).toLowerCase() : "") || (c.name ? c.name.toLowerCase() : "");
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(c);
  }
  return out;
}

/* ======================= Constants ======================= */
const DEBOUNCE = 120;
const MAX_RESULTS = 40;
const NAV_HEIGHT = 80;
const QS_HEADER_H = 48; // height (px) of the sticky header/search bar

// ADD: normalize name and group prints helpers
function normalizeName(n?: string | null) {
  return String(n || "").toLowerCase().trim();
}
function groupPrintsByName(cards: any[]) {
  const byName = new Map<string, any[]>();
  for (const c of cards || []) {
    const key =
      normalizeName(c?.name) ||
      normalizeName(c?.card_faces?.[0]?.name);
    if (!key) continue;
    const arr = byName.get(key) || [];
    arr.push(c);
    byName.set(key, arr);
  }
  return byName;
}

// ADD: search with unique=prints, group by name, keep newest as representative
async function searchGroupedByName(term: string, signal?: AbortSignal) {
  try {
    // Prefer backend-hosted index for near-instant results
    // If multi-token (3+ letters) present, build AND-style name query: name:t1 name:t2 ...
    const tokens = normalizeHuman(term).toLowerCase().split(/\s+/).map(squishAlphaNum).filter(t => t && t.length >= 3);
    const andQuery = tokens.length >= 2 ? tokens.map(t => `name:${t}`).join(" ") : term;
    const raw = await cardsSearch(
      { q: andQuery, limit: 200, unique: "prints", order: "released", dir: "desc" } as any,
      signal
    );
    const payload = await toJson(raw);
    const all = extractCards(payload);
    if (!Array.isArray(all) || all.length === 0) return [];

    const grouped = groupPrintsByName(all);
    const rows: any[] = [];
    for (const [, arr] of grouped) {
      const sorted = sortByRelease(arr);
      const rep = { ...sorted[0], _variants: sorted }; // attach the full print set
      rows.push(rep);
    }
    // Keep newest groups first by representative
    rows.sort((a, b) => {
      const da = a?.released_at ? Date.parse(a.released_at) : 0;
      const db = b?.released_at ? Date.parse(b.released_at) : 0;
      return db - da;
    });
    return rows;
  } catch {
    return [];
  }
}

// Client-side AND filter across tokens (fast, punctuation-insensitive)
function postFilterByTokens(rows: any[], term: string): any[] {
  const norm = normalizeHuman(term).toLowerCase();
  const tokens = norm.split(/\s+/).map(squishAlphaNum).filter(t => t && t.length >= 3);
  if (tokens.length === 0) return rows;
  const match = (name?: string | null): boolean => {
    const s = squishAlphaNum((name || "").toLowerCase());
    return tokens.every(t => s.includes(t));
  };
  return rows.filter(r => match(r?.name || r?.card_faces?.[0]?.name || ""));
}

// --- Backend-only helpers (use your /api/scry routes) ---
function isUuid(v?: string | null) {
  return !!v && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(v);
}
function sortByRelease<T extends { released_at?: string; set?: string; collector_number?: string }>(list: T[]) {
  return [...(list || [])].sort((a, b) => {
    const da = a.released_at ? Date.parse(a.released_at) : 0;
    const db = b.released_at ? Date.parse(b.released_at) : 0;
    if (db !== da) return db - da;
    const sa = a.set ?? "";
    const sb = b.set ?? "";
    if (sb !== sa) return sb.localeCompare(sa);
    const ca = parseInt(a.collector_number || "0", 10) || 0;
    const cb = parseInt(b.collector_number || "0", 10) || 0;
    return cb - ca;
  });
}
async function pickFirstCard(payload: any): Promise<ScryCard | null> {
  if (!payload) return null;
  if (payload.object === "card") return payload as any;
  if (payload.data?.object === "card") return payload.data as any;
  if (payload?.data && payload.data.id && payload.data.name) return payload.data as any;
  if (Array.isArray(payload?.data)) return (payload.data[0] ?? null) as any;
  if (Array.isArray(payload)) return (payload[0] ?? null) as any;
  if (payload?.id && payload?.name) return payload as any;
  return null;
}

// Parse backend payload into an array of cards
function extractCards(payload: any): ScryCard[] {
  try {
    if (typeof payload === "string") payload = JSON.parse(payload);
    if (typeof payload?.data === "string") {
      try { payload.data = JSON.parse(payload.data); } catch {}
    }
  } catch {}

  if (!payload) return [];

  if (payload?.object === "card" && payload?.id) return [payload as ScryCard];
  if (payload?.data?.object === "card" && payload?.data?.id) return [payload.data as ScryCard];

  if (Array.isArray(payload)) return payload as ScryCard[];
  if (Array.isArray(payload?.data)) return payload.data as ScryCard[];

  if (payload?.object === "list" && Array.isArray(payload?.data)) return payload.data as ScryCard[];
  if (payload?.data?.object === "list" && Array.isArray(payload?.data?.data)) return payload.data.data as ScryCard[];

  if (Array.isArray(payload?.data?.data)) return payload.data.data as ScryCard[];
  if (Array.isArray(payload?.data?.cards)) return payload.data.cards as ScryCard[];

  return [];
}

// Public Scryfall (last-resort only)
const sf = {
  async search(q: string, signal?: AbortSignal) {
    const u = new URL("https://api.scryfall.com/cards/search");
    u.searchParams.set("q", q);
    u.searchParams.set("order", "released");
    u.searchParams.set("unique", "cards");
    const r = await fetch(u.toString(), { signal });
    if (!r.ok) throw new Error(`sf search ${r.status}`);
    return r.json();
  },
  async getById(id: string, signal?: AbortSignal) {
    const r = await fetch(`https://api.scryfall.com/cards/${id}`, { signal });
    if (!r.ok) throw new Error(`sf get ${r.status}`);
    return r.json();
  },
  async printsById(id: string, signal?: AbortSignal) {
    const r = await fetch(`https://api.scryfall.com/cards/${id}/prints`, { signal });
    if (!r.ok) throw new Error(`sf prints ${r.status}`);
    return r.json();
  },
};

// Fully hydrate for V2Modal (prefer backend; fallback to public Scryfall if needed)
async function fetchBundleForModal(
  ref: { scryfall_id?: string | null; oracle_id?: string | null; name?: string | null },
  signal?: AbortSignal
): Promise<{ card: ScryCard | null; prints: ScryCard[]; rulings: any[] }> {
  let card: ScryCard | null = null;
  let prints: ScryCard[] = [];
  let rulings: any[] = [];

  // 1) Prefer oracle_id → id → name (backend)
  if (ref.oracle_id) {
    try {
      const payload = await apiSearch(`oracleid:${ref.oracle_id}`, signal);
      const arr = extractCards(payload);
      prints = sortByRelease(arr);
      card = prints[0] ?? null;
    } catch {}
  }
  if (!card && ref.scryfall_id) {
    try {
      const payload = await getCardById(ref.scryfall_id, signal);
      card = await pickFirstCard(payload);
    } catch {}
  }
  if (!card && ref.name) {
    try {
      const nm = ref.name.replace(/"/g, '\\"');
      const payload = await apiSearch(`name:"${nm}"`, signal);
      const arr = extractCards(payload);
      card = sortByRelease(arr)[0] ?? null;
    } catch {}
  }

  // 2) Prints (backend)
  if (!prints.length && card?.id) {
    try {
      const payload = await printsById(card.id, signal);
      prints = sortByRelease(extractCards(payload));
    } catch {}
  }
  if (!prints.length && card?.oracle_id) {
    try {
      const list = await backendPrintsForCard(card, signal);
      prints = list;
    } catch {}
  }

  // 3) Public Scryfall fallback (only if backend failed)
  if (!card && ref.oracle_id) {
    try {
      const payload = await sf.search(`oracleid:${ref.oracle_id}`, signal);
      const arr = extractCards(payload);
      prints = sortByRelease(arr);
      card = prints[0] ?? null;
    } catch {}
  }
  if (!card && ref.scryfall_id) {
    try {
      const payload = await sf.getById(ref.scryfall_id, signal);
      card = await pickFirstCard(payload);
    } catch {}
  }
  if (!card && ref.name) {
    try {
      const nm = ref.name.replace(/"/g, '\\"');
      const payload = await sf.search(`name:"${nm}"`, signal);
      const arr = extractCards(payload);
      card = sortByRelease(arr)[0] ?? null;
    } catch {}
  }
  if (!prints.length && card?.id) {
    try {
      const payload = await sf.printsById(card.id, signal);
      prints = sortByRelease(extractCards(payload));
    } catch {}
  }

  if (!prints.length && card) prints = [card];

  // Rulings (backend first, then public)
  const rulingsId = card?.id || prints[0]?.id || null;
  if (rulingsId) {
    try {
      const payload = await rulingsById(rulingsId, signal);
      rulings = extractCards(payload) as any[];
    } catch {
      try {
        const payload = await sf.rulingsById(rulingsId, signal);
        rulings = (Array.isArray(payload?.data) ? payload.data : (Array.isArray(payload) ? payload : [])) as any[];
      } catch {}
    }
  }

  // Dedupe prints by id
  if (prints.length) {
    const seen = new Set<string>();
    prints = prints.filter(c => (c?.id && !seen.has(c.id) ? (seen.add(c.id), true) : false));
  }

  return { card, prints, rulings };
}

// Helper: fetch variants by oracle_id via backend API (unique=prints)
async function fetchVariantsByOracle(oracleId: string, signal?: AbortSignal) {
  const raw = await cardsSearch(
    { q: `oracleid:${oracleId}`, unique: "prints", limit: 200, order: "released", dir: "desc" } as any,
    signal
  );
  const json = await toJson(raw);
  const arr = extractCards(json);
  return sortByRelease(arr);
}

// Helper: fetch variants by exact name (unique=prints), newest → oldest
async function fetchVariantsByNameExact(name: string, signal?: AbortSignal) {
  const raw = await cardsSearch(
    { q: `!"${String(name || "").replace(/"/g, '\\"')}"`, limit: 200, unique: "prints", order: "released", dir: "desc" } as any,
    signal
  );
  const payload = await toJson(raw);
  const arr = extractCards(payload);
  return sortByRelease(arr);
}

/* ======================= Component ======================= */

export default function QuickSearchOverlay() {
  const [open, setOpen] = useState<boolean>(false);
  const [q, setQ] = useState("");
  const [pending, setPending] = useState(false);
  const [cards, setCards] = useState<ScryCard[]>([]);
  // Track current face index for modal double-faced cards (DFC) by Scryfall id
  const [faceIndexById, setFaceIndexById] = useState<Record<string, number>>({});
  const [wishlistIds, setWishlistIds] = useState<Set<string>>(new Set());
  const wishlistedCount = useMemo(
    () => cards.reduce((n, c) => n + (wishlistIds.has(c.id) ? 1 : 0), 0),
    [cards, wishlistIds]
  );
  const [selectedCard, setSelectedCard] = useState<ScryCard | null>(null);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const onTradesPage = useMemo(() => location.pathname.startsWith("/trades"), [location.pathname]);
  const onCommunityPage = useMemo(() => location.pathname.startsWith("/community"), [location.pathname]);
  const onCollectionPage = useMemo(
    () => location.pathname.includes("/collection") || location.pathname.includes("?tab=collection"),
    [location.pathname]
  );

  const [listings, setListings] = useState<ListingCard[]>([]);
  const [listingsLoading, setListingsLoading] = useState(false);
  const listingsLoadedRef = useRef(false);
  const [ownerUsernames, setOwnerUsernames] = useState<Record<string, string>>({});

  const [resultsCollapsed, setResultsCollapsed] = useState(false);
  const [expanded, setExpanded] = useState<{ name: string; variants: ScryCard[] } | null>(null);
  // Track whether the expanded variants grid is shown
  const [gridExpanded, setGridExpanded] = useState<boolean>(false);
  const hasOverflow = useMemo(() => (!expanded && cards.length > 10), [expanded, cards.length]);

  const abortRef = useRef<AbortController | null>(null);
  const debounceRef = useRef<number | null>(null);
  const rowRef = useRef<HTMLDivElement | null>(null);
  const drag = useRef<{ x: number; sc: number; moved: boolean; type: string } | null>(null);

  const broadcast = useCallback((state: boolean) => {
    window.dispatchEvent(new CustomEvent("app:quickSearchState", { detail: { open: state } }));
  }, []);

  /* -------- open/close via global events (search button) -------- */
  useEffect(() => {
    function onToggle() {
      setOpen((prev) => {
        if (prev) {
          // Close: reset UI and broadcast
          resetCore();
          broadcast(false);
          return false;
        } else {
          // Open fresh
          broadcast(true);
          setTimeout(() => inputRef.current?.focus(), 15);
          return true;
        }
      });
    }
    window.addEventListener("app:toggleQuickSearch", onToggle as any);
    return () => window.removeEventListener("app:toggleQuickSearch", onToggle as any);
  }, [broadcast]);

  useEffect(() => {
    function onOpen() {
      setOpen(true);
      broadcast(true);
      setTimeout(() => inputRef.current?.focus(), 10);
    }
    function onClose() {
      // Close: reset UI and close overlay
      resetCore();
      setOpen(false);
      broadcast(false);
    }
    window.addEventListener("app:openQuickSearch", onOpen as any);
    window.addEventListener("app:closeQuickSearch", onClose as any);
    return () => {
      window.removeEventListener("app:openQuickSearch", onOpen as any);
      window.removeEventListener("app:closeQuickSearch", onClose as any);
    };
  }, [broadcast]);

  useEffect(() => {
    function esc(e: KeyboardEvent) {
      if (e.key === "Escape" && open) {
        window.dispatchEvent(new Event("app:closeQuickSearch"));
      }
    }
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [open]);

  /* ----------------- Debounced backend search ----------------- */
  useEffect(() => {
    if (!open) return;
    const term = q.trim();
    // Default to single-row view whenever a new term is entered
    setGridExpanded(false);
    if (term.length < 2) {
      setCards([]);
      setPending(false);
      abortRef.current?.abort();
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
      return;
    }

    setPending(true);
    abortRef.current?.abort();
    if (debounceRef.current) window.clearTimeout(debounceRef.current);

    const ac = new AbortController();
    abortRef.current = ac;

    const t = window.setTimeout(async () => {
      try {
        // NEW: grouped-by-name search using unique=prints
        const grouped = await searchGroupedByName(term, ac.signal);
        if (Array.isArray(grouped) && grouped.length > 0) {
          setCards(grouped.slice(0, 24));
        } else {
          // Fallback: original path (non-grouped)
          const raw = await cardsSearch({ q: term, limit: 24 } as any, ac.signal);
          const payload = await toJson(raw);
          const base = extractCards(payload) as any[];
          setCards(base || []);
        }
      } catch (e) {
        console.error("[quicksearch] backend card search failed:", e);
        setCards([]); // only clear on backend failure
      } finally {
        setPending(false);
      }

      // Secondary listings fetch: never clear primary results
      try {
        await fetchAndSetListings(term, ac.signal);
      } catch (e) {
        console.warn("[quicksearch] listings query failed:", e);
      }
    }, 200);

    debounceRef.current = t;
    return () => {
      ac.abort();
      window.clearTimeout(t);
    };
  }, [q, open]);

  /* ----------------- Wishlist status preload ----------------- */
  useEffect(() => {
    let cancel = false;
    async function load() {
      const u = auth.currentUser;
      if (!u || cards.length === 0) {
        if (!cancel) setWishlistIds(new Set());
        return;
      }
      const ids = cards.map((c) => c.id);
      const found = new Set<string>();
      for (let i = 0; i < ids.length; i += 10) {
        const chunk = ids.slice(i, i + 10);
        try {
          const snap = await getDocs(
            query(collection(db, "users", u.uid, "wishlist"), where("scryfall_id", "in", chunk))
          );
          snap.forEach((d) => {
            const v: any = d.data();
            if (v.scryfall_id) found.add(v.scryfall_id);
          });
        } catch {
          /* ignore */
        }
      }
      if (!cancel) setWishlistIds(found);
    }
    load();
    return () => { cancel = true; };
  }, [cards]);

  /* ----------------- Touch drag row helpers ----------------- */
  const onPointerDown = (e: React.PointerEvent) => {
    if (!rowRef.current) return;
    if (e.pointerType === "touch" || e.pointerType === "pen") {
      drag.current = { x: e.clientX, sc: rowRef.current.scrollLeft, moved: false, type: e.pointerType };
      rowRef.current.setPointerCapture(e.pointerId);
    } else {
      drag.current = null;
    }
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!drag.current || !rowRef.current) return;
    if (drag.current.type !== "touch" && drag.current.type !== "pen") return;
    const dx = e.clientX - drag.current.x;
    if (Math.abs(dx) > 14) drag.current.moved = true;
    rowRef.current.scrollLeft = drag.current.sc - dx;
  };
  const onPointerUp = (e: React.PointerEvent) => {
    if (!drag.current || !rowRef.current) return;
    if (drag.current.type === "touch" || drag.current.type === "pen") rowRef.current.releasePointerCapture(e.pointerId);
    drag.current = null;
  };

  // Overflow affordance handled via heuristic `hasOverflow` (no DOM reads here).

  /* ----------------- Per-page filtering integration ----------------- */
  useEffect(() => { setResultsCollapsed(onCommunityPage); }, [onCommunityPage]);

  const broadcastFilter = useCallback((term: string) => {
    if (onCommunityPage) {
      window.dispatchEvent(new CustomEvent("app:communityFilter", { detail: { term } }));
    } else if (onCollectionPage) {
      window.dispatchEvent(new CustomEvent("app:collectionFilter", { detail: { term } }));
      window.dispatchEvent(new CustomEvent("app:wishlistFilter", { detail: { term } }));
    }
  }, [onCommunityPage, onCollectionPage]);

  useEffect(() => { if (open) broadcastFilter(q.trim()); }, [q, open, broadcastFilter]);

  const qTrim = q.trim();
  const showRows = open;
  const filteringPage = onCommunityPage || onCollectionPage;
  const canCollapseResults = filteringPage;
  const meUid = auth.currentUser?.uid || null;

  /* ----------------- Internal Listings row ----------------- */
  const internalMatches = useMemo(() => {
    if (!qTrim || qTrim.startsWith("@") || qTrim.length < 2) return [];
    if (!listings.length) return [];
    const tokens = qTrim.toLowerCase().split(/\s+/).filter(Boolean);
    if (!tokens.length) return [];
    return listings.filter((l) => {
      if (meUid && l.ownerUid === meUid) return false;
      const nm = (l.name || "").toLowerCase();
      if (!nm) return false;
      return tokens.every((t) => nm.includes(t));
    });
  }, [qTrim, listings, meUid]);

  const showInternalRow =
    !!qTrim && !qTrim.startsWith("@") && qTrim.length >= 2 && internalMatches.length > 0;

  useEffect(() => {
    try {
      const stored = localStorage.getItem("qso_collapsed:" + location.pathname);
      if (stored === "true") setResultsCollapsed(true);
      else if (stored === "false") setResultsCollapsed(false);
      else if (filteringPage) setResultsCollapsed(true);
    } catch {}
  }, [location.pathname, filteringPage]);

  useEffect(() => {
    try {
      localStorage.setItem("qso_collapsed:" + location.pathname, resultsCollapsed ? "true" : "false");
    } catch {}
  }, [resultsCollapsed, location.pathname]);

  useEffect(() => {
    if (!filteringPage) return;
    const term = qTrim;
    const handle = window.setTimeout(() => {
      if (onCollectionPage) {
        window.dispatchEvent(new CustomEvent("app:collectionFilter", { detail: { term } }));
        window.dispatchEvent(new CustomEvent("app:wishlistFilter", { detail: { term } }));
      }
    }, 300);
    return () => window.clearTimeout(handle);
  }, [qTrim, filteringPage, onCollectionPage]);

  /* ----------------- Load global listings (sale/trade) ----------------- */
  const loadGlobalListings = useCallback(async (force = false) => {
    if (listingsLoading) return;
    if (listingsLoadedRef.current && !force) return;
    setListingsLoading(true);
    try {
      const L = 120;
      const cg = collectionGroup(db, "cards");
      const qSale = query(cg, where("status", "==", "sale"), limit(L));
      const qTrade = query(cg, where("status", "==", "trade"), limit(L));

      const [s1, s2] = await Promise.allSettled([getDocs(qSale), getDocs(qTrade)]);
      const batch: ListingCard[] = [];

      const harvest = (res: any, status: "sale" | "trade") => {
        if (res.status !== "fulfilled") {
          console.warn("[quicksearch] listings query failed:", res.reason);
          return;
        }
        res.value.forEach((d: any) => {
          const v = d.data() || {};
          const parts = d.ref.path.split("/");
          const i = parts.indexOf("users");
          const ownerUid = i >= 0 ? parts[i + 1] : undefined;
          if (!ownerUid) return;

          batch.push({
            id: d.id,
            ownerUid,
            ownerUsername: v.ownerUsername || null,
            name: v.name || v.card_name || "Card",
            image:
              v.image ||
              v.card_image ||
              v.custom_image_url ||
              v.scryfall_full?.image_uris?.normal ||
              v.scryfall_full?.image_uris?.small ||
              null,
            status,
            scryfall_id: v.scryfall_id || null,
            oracle_id: v.oracle_id || null,
          });
        });
      };

      harvest(s1, "sale");
      harvest(s2, "trade");

      const seen = new Set<string>();
      const dedup = batch.filter((b) => {
        const key = `${b.ownerUid}:${b.oracle_id || b.id}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      dedup.sort((a, b) => a.name.localeCompare(b.name));
      setListings(dedup.slice(0, 100));
      listingsLoadedRef.current = true;

      const need = Array.from(new Set(dedup.map((d) => d.ownerUid))).slice(0, 30);
      await Promise.all(
        need.map(async (uid) => {
          try {
            const snap = await getDoc(doc(db, "users", uid));
            const u = snap.exists() ? (snap.data() as any) : {};
            setOwnerUsernames((prev) => ({
              ...prev,
              [uid]: u?.username || u?.display_name || uid.slice(0, 8),
            }));
          } catch (e) {
            console.warn("[quicksearch] user load failed", uid, e);
          }
        })
      );
    } catch (e) {
      console.warn("[quicksearch] loadGlobalListings fatal:", e);
    } finally {
      setListingsLoading(false);
    }
  }, [listingsLoading]);

  useEffect(() => { if (open) void loadGlobalListings(); }, [open, loadGlobalListings]);

  /* ----------------- Modal state + open handler (GOLDEN) ----------------- */
const [modalOpen, setModalOpen] = useState(false);
const [modalBase, setModalBase] = useState<{ scryfall_id?: string | null; oracle_id?: string | null; name?: string | null } | null>(null);
const [modalPreload, setModalPreload] = useState(null);

// Central reset: clear search UI state and abort in-flight work (keeps modal)
function resetCore() {
  try { abortRef.current?.abort(); } catch {}
  if (debounceRef.current) { window.clearTimeout(debounceRef.current); debounceRef.current = null; }
  setPending(false);
  setQ("");
  setCards([]);
  setExpanded(null);
  setGridExpanded(false);
  setWishlistIds(new Set());
  setListings([]);
  setOwnerUsernames({});
  setResultsCollapsed(false);
}

function resetAndClose() {
  resetCore();
  setOpen(false);
  window.dispatchEvent(new CustomEvent("app:quickSearchState", { detail: { open: false } }));
}

// NEW: expand a result into its variants in-row instead of opening the modal
async function expandToVariants(hit: any) {
  // Prefer attached _variants; then name-exact; then oracle_id
  let variants: ScryCard[] = Array.isArray(hit?._variants) ? hit._variants : [];
  if (!variants.length && hit?.name) {
    try { variants = await fetchVariantsByNameExact(hit.name, undefined); } catch {}
  }
  if (!variants.length && hit?.oracle_id) {
    try { variants = await fetchVariantsByOracle(hit.oracle_id, undefined); } catch {}
  }
  if (!variants.length) variants = [hit];

  // Dedupe by id and sort newest → oldest
  const seen = new Set<string>();
  const uniq = variants.filter((c: any) => (c?.id && !seen.has(c.id) ? (seen.add(c.id), true) : false));
  setExpanded({ name: hit?.name || "Card", variants: sortByRelease(uniq) });
}

// NEW: clicking a specific variant opens V2Modal and resets the search UI
async function openVariantInModal(variant: any, allVariants: any[]) {
  const preload: any = {
    card: variant,
    prints: allVariants,
    variants: allVariants,
    rulings: Array.isArray(variant?.rulings) ? variant.rulings : undefined,
  };
  setModalPreload(preload);
  setModalBase({
    scryfall_id: variant?.id || null,
    oracle_id: variant?.oracle_id || null,
    name: variant?.name || null,
  });
  setModalOpen(true);

  // Clear just the quicksearch row/input so the modal has a clean backdrop
  if (typeof resetForModal === "function") resetForModal();
}

// When opening a card (ensure we pass real backend ids to the modal)
async function openCard(hit: any) {
  const ac = new AbortController();
  try {
    const ref = {
      scryfall_id: hit?.id || hit?.scryfall_id || null,
      oracle_id: hit?.oracle_id || null,
      name: hit?.name || null,
    };
    const { card, prints, rulings } = await fetchBundleForModal(ref, ac.signal);

    // Prefer variants attached by grouped search; fallback to bundle prints
    let variants: any[] = Array.isArray(hit?._variants) ? hit._variants : (Array.isArray(prints) ? prints : []);

    // Safety fallback if still empty/singleton: fan out by oracle_id
    if ((variants.length <= 1) && (card?.oracle_id || ref.oracle_id)) {
      try {
        variants = await fetchVariantsByOracle(card?.oracle_id || ref.oracle_id!, ac.signal);
      } catch {}
    }

    if (card?.id || ref.scryfall_id) {
      const baseCardId = card?.id || ref.scryfall_id!;
      const preload: any = {
        card: card || hit,
        prints: variants,
        variants,
        rulings,
      };
      setModalPreload(preload);
      setModalBase({
        scryfall_id: baseCardId,
        oracle_id: card?.oracle_id || ref.oracle_id || null,
        name: card?.name || ref.name || null,
      });
    } else {
      setModalPreload(Array.isArray(variants) && variants.length ? { card: hit, prints: variants, variants } : null);
      setModalBase(ref);
    }
    setModalOpen(true);
  } catch (e: any) {
    if (e?.name === "AbortError") return;
    setModalPreload(null);
    setModalBase({
      scryfall_id: hit?.id || null,
      oracle_id: hit?.oracle_id || null,
      name: hit?.name || null,
    });
    setModalOpen(true);
  }
  return () => ac.abort();
}

const closeModal = useCallback(() => {
  setModalOpen(false);
  setModalBase(null);
  setModalPreload(null);
}, []);

  // Optional: callback after V2Modal adds a card
  const onModalAdded = useCallback(() => {}, []);

  /* ----------------- Background & rendering ----------------- */
  const qTrimmed = q.trim();
  const showResults = open || modalOpen;

  // Helper: determine current face index for a card
  const getFaceIndex = useCallback((c: ScryCard) => {
    const idx = faceIndexById[c.id] ?? 0;
    const max = Array.isArray(c.card_faces) ? Math.max(0, c.card_faces.length - 1) : 0;
    return Math.min(idx, max);
  }, [faceIndexById]);

  // Helper: get display image URL for current face (falls back gracefully)
  const getImageUrlFor = useCallback((c: ScryCard, size: "small" | "normal" | "large" = "small") => {
    const idx = getFaceIndex(c);
    const faces = Array.isArray(c.card_faces) ? c.card_faces : null;
    const faceImg = faces?.[idx]?.image_uris?.[size] || faces?.[idx]?.image_uris?.normal || null;
    const singleImg = c.image_uris?.[size] || c.image_uris?.normal || null;
    return faceImg || singleImg || (c.id ? scryImageUrl(c.id, size) : null);
  }, [getFaceIndex]);

  // Action: flip to the next face for a DFC
  const flipCardFace = useCallback((c: ScryCard) => {
    const faces = Array.isArray(c.card_faces) ? c.card_faces : null;
    if (!faces || faces.length < 2) return;
    setFaceIndexById(prev => {
      const cur = prev[c.id] ?? 0;
      const next = (cur + 1) % faces.length;
      return { ...prev, [c.id]: next };
    });
  }, []);

  // Clicking backdrop: fully reset UI and close overlay
  function resetAndClose() {
    try { abortRef.current?.abort(); } catch {}
    if (debounceRef.current) { window.clearTimeout(debounceRef.current); debounceRef.current = null; }
    setPending(false);
    setQ("");
    setCards([]);
    setExpanded(null);
    setWishlistIds(new Set());
    setListings([]);
    setOwnerUsernames({});
    setResultsCollapsed(false);

    // Close modal and overlay
    setModalOpen(false);
    setModalBase(null);
    setModalPreload(null);
    setOpen(false);
    broadcast(false);
  }

  return showResults ? (
    <>
      {/* backdrop to close */}
      <div className="fixed inset-0 z-[115]" onClick={resetAndClose} />

      <div className="fixed inset-x-0 z-[120] flex flex-col gap-2 px-2 pb-2" style={{ bottom: NAV_HEIGHT }}>
        {/* RESULTS STRIP */}
        <div className="relative">
          {resultsCollapsed ? (
            <button
              onClick={() => setResultsCollapsed(false)}
              className="w-full flex items-center justify-between gap-3 rounded-lg border border-white/15 bg-black/70 px-3 py-2 text-left text-xs text-white/70 hover:bg-black/60"
              title="Show quick search results"
            >
              <span className="truncate">
                {pending ? "Searching…" : cards.length ? `${cards.length} card result${cards.length === 1 ? "" : "s"}` : "No card results"}
                {wishlistedCount ? ` • ${wishlistedCount} wishlisted` : ""}
                {internalMatches.length ? ` • ${internalMatches.length} listing${internalMatches.length === 1 ? "" : "s"}` : ""}
                {qTrimmed ? ` • “${qTrimmed.slice(0, 24)}${qTrimmed.length > 24 ? "…" : ""}”` : ""}
              </span>
              <span className="text-white/60 text-[10px] uppercase tracking-wide">Open ▲</span>
            </button>
          ) : (
            <>
              {/* NEW: header bar when showing variants */}
              {expanded && (
                <div className="flex items-center justify-between px-2 py-1 text-xs text-white/80">
                  <div className="truncate">
                    Variants for “{expanded.name}” • {expanded.variants.length}
                  </div>
                  <button
                    onClick={() => setExpanded(null)}
                    className="px-2 py-1 rounded border border-white/15 bg-white/5 hover:bg-white/10"
                  >
                    Back
                  </button>
                </div>
              )}

              {/* Row header with Expand toggle when applicable */}
              {!expanded && (
                <div className="flex items-center justify-end px-2 pb-1 text-[11px]">
                  {hasOverflow && !gridExpanded && (
                    <button
                      onClick={() => setGridExpanded(true)}
                      className="px-2 py-1 rounded border border-white/15 bg-white/5 hover:bg-white/10 text-white/70 min-w-[76px] text-center"
                      title="Expand results"
                    >
                      Expand
                    </button>
                  )}
                </div>
              )}

              {!gridExpanded || expanded ? (
                <div
                  ref={rowRef}
                  onPointerDown={onPointerDown}
                  onPointerMove={onPointerMove}
                  onPointerUp={onPointerUp}
                  className="relative flex gap-3 overflow-x-auto no-scrollbar snap-x snap-mandatory py-1 px-1 rounded-lg border border-white/15 bg-black/75 animate-[slideDownFade_0.2s_ease-out]"
                  style={{ WebkitOverflowScrolling: "touch" }}
                  role="list"
                >
                  {/* Horizontal scroll assist arrows (non-touch hover) */}
                  {!expanded && hasOverflow && (
                    <>
                      <button
                        type="button"
                        onClick={() => { const el = rowRef.current; if (el) el.scrollBy({ left: -300, behavior: "smooth" }); }}
                        className="hidden md:inline-flex absolute left-1 top-1/2 -translate-y-1/2 h-7 w-7 items-center justify-center rounded-full bg-black/60 border border-white/25 text-white/90 hover:bg-black/50"
                        aria-label="Scroll left"
                      >
                        ◀
                      </button>
                      <button
                        type="button"
                        onClick={() => { const el = rowRef.current; if (el) el.scrollBy({ left: 300, behavior: "smooth" }); }}
                        className="hidden md:inline-flex absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 items-center justify-center rounded-full bg-black/60 border border-white/25 text-white/90 hover:bg-black/50"
                        aria-label="Scroll right"
                      >
                        ▶
                      </button>
                    </>
                  )}
                {pending && !expanded && (
                  <div className="flex items-center justify-center min-w-[120px] h-28 rounded-lg bg-white/5 border border-white/10 text-sm text-white/60 px-3">
                    Searching…
                  </div>
                )}

                {/* NEW: variants view */}
                {expanded
                  ? expanded.variants.map((v) => {
                      const img = getImageUrlFor(v, "small");
                      const isWish = wishlistIds.has(v.id);
                      const isDfc = Array.isArray(v.card_faces) && v.card_faces.length >= 2;
                      const faceName = isDfc ? (v.card_faces?.[getFaceIndex(v)] as any)?.name || v.name : v.name;
                      return (
                        <button
                          key={v.id}
                          onClick={() => openVariantInModal(v, expanded.variants)}
                          disabled={isWish}
                          className={[
                            "snap-start w-[118px] flex-shrink-0 transition relative",
                            isWish ? "opacity-70 cursor-not-allowed" : "hover:scale-[1.02]",
                          ].join(" ")}
                          role="listitem"
                          title={`${faceName ?? "Card"} • ${(v.set_name || v.set || "").toString().toUpperCase()} #${v.collector_number || ""}`}
                        >
                          <div className="aspect-[3/4] w-full overflow-hidden rounded-md relative">
                            {img ? (
                              <img
                                src={img}
                                alt={faceName ?? "Card"}
                                className="h-full w-full object-cover select-none pointer-events-none"
                                draggable={false}
                                loading="lazy"
                              />
                            ) : (
                              <div className="h-full w-full flex items-center justify-center text-[10px] text-white/40 bg-white/5">
                                {faceName}
                              </div>
                            )}
                            <div className="absolute left-1 bottom-1 text-[9px] text-white/90 bg-black/70 rounded px-1 py-[2px] border border-white/20 pointer-events-none">
                              {(v.set || "").toString().toUpperCase()}#{v.collector_number || ""}
                            </div>
                            {isDfc && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  flipCardFace(v);
                                }}
                                className="absolute right-1 top-1/2 -translate-y-1/2 inline-flex items-center justify-center h-6 w-6 rounded-full bg-black/70 border border-white/25 text-white/90 hover:bg-black/60"
                                aria-label="Flip card face"
                                title="Flip card face"
                              >
                                  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" className="opacity-90">
                                  <path
                                    fill="currentColor"
                                    d="M17.65 6.35A7.95 7.95 0 0 0 12 4V1L7 6l5 5V8c2.76 0 5 2.24 5 5 0 .65-.13 1.27-.36 1.83l1.53 1.53A7.96 7.96 0 0 0 20 13c0-2.21-.9-4.21-2.35-5.65zM6.35 17.65A7.95 7.95 0 0 0 12 20v3l5-5-5-5v3c-2.76 0-5-2.24-5-5 0-.65.13-1.27.36-1.83L5.83 7.59A7.96 7.96 0 0 0 4 11c0 2.21.9 4.21 2.35 5.65z"
                                  />
                                </svg>
                              </button>
                            )}
                            {isWish && (
                              <span
                                className="absolute top-1 left-1 inline-flex items-center justify-center rounded px-1.5 py-[1px] text-[10px] font-semibold bg-pink-500/90 text-white shadow pointer-events-none"
                                aria-hidden="true"
                              >
                                ♥
                              </span>
                            )}
                          </div>
                        </button>
                      );
                    })
                  : // ORIGINAL: grouped search results view (unchanged except onClick)
                    cards.length > 0 ? (
                      cards.slice(0, 18).map((c) => {
                        const img = getImageUrlFor(c, "small");
                        const isWishlisted = wishlistIds.has(c.id);
                        const isDfc = Array.isArray(c.card_faces) && c.card_faces.length >= 2;
                        const faceName = isDfc ? (c.card_faces?.[getFaceIndex(c)] as any)?.name || c.name : c.name;

                        return (
                          <button
                            key={c.id || c.oracle_id || c.name}
                            onClick={() => expandToVariants(c)}
                            className="snap-start w-[118px] flex-shrink-0 hover:scale-[1.02] transition relative"
                            role="listitem"
                            aria-label={`Open ${faceName ?? "Card"}${isWishlisted ? " (wishlisted)" : ""}`}
                            title={`${faceName ?? "Card"}${isWishlisted ? " — wishlisted" : ""}`}
                          >
                            <div
                              className={[
                                "aspect-[3/4] w-full overflow-hidden rounded-md relative",
                                isWishlisted ? "ring-1 ring-pink-400/60" : "",
                              ].join(" ")}
                            >
                              {img ? (
                                <img
                                  src={img}
                                  alt={faceName ?? "Card"}
                                  className="h-full w-full object-cover select-none pointer-events-none"
                                  draggable={false}
                                  loading="lazy"
                                />
                              ) : (
                                <div className="h-full w-full flex items-center justify-center text-[10px] text-white/40 bg-white/5">
                                  {faceName}
                                </div>
                              )}
                              {isDfc && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    flipCardFace(c);
                                  }}
                                  className="absolute right-1 top-1/2 -translate-y-1/2 inline-flex items-center justify-center h-6 w-6 rounded-full bg-black/70 border border-white/25 text-white/90 hover:bg-black/60"
                                  aria-label="Flip card face"
                                  title="Flip card face"
                                >
                                  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" className="opacity-90">
                                    <path
                                      fill="currentColor"
                                      d="M17.65 6.35A7.95 7.95 0 0 0 12 4V1L7 6l5 5V8c2.76 0 5 2.24 5 5 0 .65-.13 1.27-.36 1.83l1.53 1.53A7.96 7.96 0 0 0 20 13c0-2.21-.9-4.21-2.35-5.65zM6.35 17.65A7.95 7.95 0 0 0 12 20v3l5-5-5-5v3c-2.76 0-5-2.24-5-5 0-.65.13-1.27.36-1.83L5.83 7.59A7.96 7.96 0 0 0 4 11c0 2.21.9 4.21 2.35 5.65z"
                                    />
                                  </svg>
                                </button>
                              )}
                            </div>

                            {isWishlisted && (
                              <span className="absolute top-1 left-1 inline-flex items-center justify-center rounded px-1.5 py-[1px] text-[10px] font-semibold bg-pink-500/90 text-white shadow" aria-hidden="true">
                                ♥
                              </span>
                            )}
                          </button>
                        );
                      })
                    ) : (
                      !pending && (
                        <div className="min-w-[160px] h-28 flex items-center justify-center rounded-lg bg-white/5 border border-white/10 text-xs text-white/50 px-3">
                          No card results
                        </div>
                      )
                    )}

                {/* Internal listings preview row: only when NOT in variants view */}
                {!expanded &&
                  !!qTrimmed &&
                  !qTrimmed.startsWith("@") &&
                  qTrimmed.length >= 2 &&
                  internalMatches.length > 0 &&
                  internalMatches.slice(0, 12).map((l) => (
                    <button
                      key={`${l.ownerUid}:${l.id}`}
                      onClick={() => {
                        if (l.scryfall_id) {
                          // keep original behavior for listings
                          openVariantInModal({ id: l.scryfall_id, oracle_id: l.oracle_id, name: l.name }, []);
                        } else {
                          navigate(`/listing/${l.id}`);
                          setOpen(false);
                        }
                      }}
                      className="snap-start min-w=[140px] flex-shrink-0 rounded-lg bg-black/60 border border-white/10 p-2"
                    >
                      <div className="text-sm text-white/90 truncate">{l.name}</div>
                      <div className="text-xs text-white/60 mt-1">{ownerUsernames[l.ownerUid] || l.ownerUid.slice(0, 8)}</div>
                    </button>
                  ))}
                </div>
              ) : (
                /* Expanded grid view */
                <>
                  {/* Single Close button just above the grid with minimal spacing */}
                  {!expanded && (
                    <div className="flex items-center justify-end px-2 mb-3 text-[11px]">
                      <button
                        onClick={() => setGridExpanded(false)}
                        className="px-2 py-1 rounded border border-white/15 bg-white/5 hover:bg-white/10 text-white/70 min-w-[76px] text-center"
                        title="Close expanded grid"
                      >
                        Close
                      </button>
                    </div>
                  )}
                  
                <div
                  className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 mt-2 px-2 pb-3 rounded-lg border border-white/15 bg-black/75 animate-[slideUpFade_0.25s_ease-out] overflow-y-auto"
                  style={{
                    maxHeight: `calc(100vh - ${NAV_HEIGHT + QS_HEADER_H + 32}px)`,
                    WebkitOverflowScrolling: "touch",
                    overscrollBehaviorY: "contain",
                    touchAction: "pan-y",
                  }}
                  role="region"
                  aria-label="Expanded search results grid"
                >
                  {cards.map((c) => {
                    const img = getImageUrlFor(c, "small");
                    const isWishlisted = wishlistIds.has(c.id);
                    const isDfc = Array.isArray(c.card_faces) && c.card_faces.length >= 2;
                    const faceName = isDfc ? (c.card_faces?.[getFaceIndex(c)] as any)?.name || c.name : c.name;
                    return (
                      <button
                        key={c.id || c.oracle_id || c.name}
                        onClick={() => expandToVariants(c)}
                        className="transition relative"
                        title={`${faceName ?? "Card"}${isWishlisted ? " — wishlisted" : ""}`}
                      >
                        <div className={["aspect-[3/4] w-full overflow-hidden rounded-md relative", isWishlisted ? "ring-1 ring-pink-400/60" : ""].join(" ")}> 
                          {img ? (
                            <img src={img} alt={faceName ?? "Card"} className="h-full w-full object-cover select-none pointer-events-none" draggable={false} loading="lazy" />
                          ) : (
                            <div className="h-full w-full flex items-center justify-center text-[10px] text-white/40 bg-white/5">{faceName}</div>
                          )}
                          {isDfc && (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); flipCardFace(c); }}
                              className="absolute right-1 top-1/2 -translate-y-1/2 inline-flex items-center justify-center h-6 w-6 rounded-full bg-black/70 border border-white/25 text-white/90 hover:bg-black/60"
                              aria-label="Flip card face"
                            >
                              <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" className="opacity-90"><path fill="currentColor" d="M17.65 6.35A7.95 7.95 0 0 0 12 4V1L7 6l5 5V8c2.76 0 5 2.24 5 5 0 .65-.13 1.27-.36 1.83l1.53 1.53A7.96 7.96 0 0 0 20 13c0-2.21-.9-4.21-2.35-5.65zM6.35 17.65A7.95 7.95 0 0 0 12 20v3l5-5-5-5v3c-2.76 0-5-2.24-5-5 0-.65.13-1.27.36-1.83L5.83 7.59A7.96 7.96 0 0 0 4 11c0 2.21.9 4.21 2.35 5.65z"/></svg>
                            </button>
                          )}
                          {isWishlisted && (
                            <span className="absolute top-1 left-1 inline-flex items-center justify-center rounded px-1.5 py-[1px] text-[10px] font-semibold bg-pink-500/90 text-white shadow" aria-hidden="true">♥</span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
                </>
              )}

              {canCollapseResults && !expanded && (
                <div className="flex items-center justify-end px-1 mt-1">
                  <button
                    onClick={() => setResultsCollapsed(true)}
                    className="text-[11px] text-white/60 hover:text-white/80 px-2 py-1 rounded"
                    aria-pressed={resultsCollapsed}
                  >
                    Hide results
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* INPUT BAR */}
        <div className="flex items-center gap-2 rounded-lg border border-white/15 bg-black/85 px-3 py-2 backdrop-blur-sm">
          <input
            ref={inputRef}
            id="quick-search-input"
            data-quicksearch-input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={
              onCommunityPage
                ? "Filter community (@user or card)…"
                : onCollectionPage
                ? "Filter collection…"
                : "Quick card search…"
            }
            className="flex-1 bg-transparent text-base text-white placeholder:text-white/40 outline-none quicksearch-input"
            inputMode="search"
            autoCorrect="off"
            spellCheck={false}
          />
          <button
            onClick={() => resetAndClose()}
            className="text-xs font-semibold px-2 py-1 rounded bg-rose-500/15 text-rose-300 hover:bg-rose-500/25 hover:text-rose-200 border border-rose-400/30 transition"
            aria-label="Reset & Close"
          >
            Reset
          </button>
        </div>
      </div>

      {modalOpen && modalBase && (
        <V2Modal
          open={modalOpen}
          baseCard={modalBase}
          onClose={() => {
            setModalOpen(false);
            // Optionally also close the overlay on modal close:
            // setOpen(false);
          }}
          onAdded={onModalAdded}
          closeOnAdd
          preload={modalPreload}
        />
      )}

      <style>{`
        .no-scrollbar::-webkit-scrollbar{display:none;}
        .quick-gold-glow{
          box-shadow:0 0 0 1px #f5c84b,
                     0 0 10px 3px #f5c84b80,
                     0 0 28px 10px #e1a90070;
          animation:quickGoldPulse 2.2s ease-in-out infinite;
        }
        /* Slide up + fade for expanded grid */
        @keyframes slideUpFade {from{opacity:0;transform:translateY(8px);}to{opacity:1;transform:translateY(0);}}
        /* Slide down + fade for collapsing back to row */
        @keyframes slideDownFade {from{opacity:0;transform:translateY(-6px);}to{opacity:1;transform:translateY(0);}}
        @keyframes quickGoldPulse {
          0%,100%{box-shadow:0 0 0 1px #f5c84b,0 0 6px 1px #f5c84b66,0 0 18px 6px #e1a90040;}
          50%{box-shadow:0 0 0 1px #ffe38d,0 0 16px 8px #ffd966aa,0 0 36px 14px #f5c84b90;}
        }
      `}</style>
    </>
  ) : null;
}

/* ======================= Helpers (moved from component) ======================= */

// Try multiple query shapes and return first matching UUID
async function findFirstIdByQueries(queries: string[], signal?: AbortSignal): Promise<string | null> {
  for (const q of queries) {
    try {
      const payload = await apiSearch(q, signal);
      const list = extractCards(await toJson(payload));
      const found = list.find((c: any) => isUuid(c?.id));
      if (found?.id) return found.id;
    } catch {}
  }
  return null;
}

// Ensure each result has a valid Scryfall UUID in id (limit extra calls)
async function ensureScryfallIds(list: ScryCard[], signal?: AbortSignal): Promise<ScryCard[]> {
  const LIMIT = 12;
  let fixes = 0;
  return Promise.all(
    list.map(async (c) => {
      if (isUuid(c.id)) return c;
      // Promote scryfall_id if present on objects coming from listings
      if (!isUuid(c.id) && isUuid((c as any)?.scryfall_id)) {
        return { ...c, id: (c as any).scryfall_id as string };
      }
      if (fixes >= LIMIT) return c;
      fixes++;
      const queries: string[] = [];
      if (c.oracle_id) queries.push(`oracleid:${c.oracle_id}`);
      if (c.name) {
        const nm = c.name.replace(/"/g, '\\"');
        queries.push(`name:"${nm}"`, nm);
      }
      const newId = queries.length ? await findFirstIdByQueries(queries, signal) : null;
      return isUuid(newId) ? ({ ...c, id: newId! } as ScryCard) : c;
    })
  );
}

// OPTIONAL: small safe helper so one failing promise doesn't nuke results
function settled<T>(p: Promise<T>): Promise<{ ok: true; value: T } | { ok: false, error: any }> {
  return p.then(v => ({ ok: true, value: v }), e => ({ ok: false, error: e }));
}

// Ensure your listings helper never throws into shared catch that clears cards
async function fetchAndSetListings(term: string, signal?: AbortSignal) {
  // ...existing Firestore query...
  // wrap inner await calls in try/catch and rethrow only if you want the warning above
}

// Normalize Response→JSON (no-op if already JSON)
function toJson<T = any>(payload: any): Promise<T> | T {
  try { if (payload && typeof payload.json === "function") return payload.json(); } catch {}
  return payload as T;
}

// Add: backend prints by oracle_id with unique=prints
async function backendPrintsForCard(ref: { id?: string; oracle_id?: string }, signal?: AbortSignal) {
  let oracleId = ref?.oracle_id || null;
  if (!oracleId && ref?.id) {
    try {
      const base = await fetch(`/${"api"}/scry/cards/${encodeURIComponent(ref.id)}`, { signal });
      if (base.ok) {
        const js = await base.json();
        oracleId = js?.oracle_id || null;
      }
    } catch {}
  }
  if (!oracleId) return [];
  const payload = await cardsSearch({ q: `oracleid:${oracleId}`, limit: 200, unique: "prints" }, signal);
  const json = await toJson(payload as any);
  const arr = Array.isArray((json as any)?.data) ? (json as any).data : [];
  // newest → oldest and keep all prints
  return arr.sort((a: any, b: any) => Date.parse(b?.released_at || "0") - Date.parse(a?.released_at || "0"));
}

async function backendSearch(query: string, ac: AbortController) {
  try {
    const payload = await cardsSearch({ q: query, limit: 24 }, ac.signal);
    // ...existing code...
  } catch (e: any) {
    if (isAbortError(e)) return;
    console.warn("[quicksearch] backend card search failed:", e);
  }
}

const isAbortError = (e: any) =>
  e?.name === "AbortError" || e?.code === 20 || /aborted/i.test(String(e?.message || ""));
